import {
  type GridCell,
  GridCellKind,
  type Theme,
} from "@glideapps/glide-data-grid";
import { getAiSpreadsheetCellDisplayValue } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/cell-display-value";
import { getSafeHyperlinkUrl } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/hyperlink";
import { volcaColor } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/spreadsheet-theme";
import { buildSuggestedValueGridCell } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/suggested-value-cell";
import { getCellErrorCode } from "@/payroll/ai-script/ai-script-spreadsheet-viewer/utils";
import type { IAiSpreadsheetCell } from "@/payroll/ai-script/types";
import {
  buildThemeOverride,
  getContentAlign,
  getCopyValue,
  getInCellEditValue,
  hasSuggestedValueOptions,
  isCellEditable,
} from "./cell-format";

const newRowThemeOverride = (): Partial<Theme> => ({
  bgCell: volcaColor("--information-subtle"),
});

export const buildNewRowCell = (): GridCell => ({
  kind: GridCellKind.Text,
  allowOverlay: true,
  readonly: false,
  displayData: "",
  data: "",
  themeOverride: newRowThemeOverride(),
});

export const buildGridCell = (
  cell: IAiSpreadsheetCell | undefined,
  editable: boolean,
  onHyperlinkClick?: (url: string) => void,
): GridCell => {
  if (!cell) {
    return {
      kind: GridCellKind.Text,
      allowOverlay: false,
      readonly: true,
      displayData: "",
      data: "",
    };
  }

  const displayData = getAiSpreadsheetCellDisplayValue(cell);
  const locked = !isCellEditable(cell);
  const hyperlinkUrl = getCellErrorCode(cell)
    ? null
    : getSafeHyperlinkUrl(cell.raw_input);

  if (editable && !locked && hasSuggestedValueOptions(cell)) {
    return buildSuggestedValueGridCell({
      value: getInCellEditValue(cell),
      suggestions: cell.suggested_values,
      contentAlign: getContentAlign(cell),
      themeOverride: buildThemeOverride(cell),
    });
  }

  if (hyperlinkUrl) {
    return {
      kind: GridCellKind.Uri,
      allowOverlay: editable && !locked,
      readonly: !editable || locked,
      displayData,
      data: getInCellEditValue(cell),
      copyData: getCopyValue(cell),
      contentAlign: getContentAlign(cell),
      hoverEffect: true,
      themeOverride: buildThemeOverride(cell),
      onClickUri: onHyperlinkClick
        ? () => onHyperlinkClick(hyperlinkUrl)
        : undefined,
    };
  }

  return {
    kind: GridCellKind.Text,
    allowOverlay: editable && !locked,
    readonly: !editable || locked,
    displayData,
    data: getInCellEditValue(cell),
    copyData: getCopyValue(cell),
    contentAlign: getContentAlign(cell),
    allowWrapping: cell.format?.alignment?.wrap_text ?? false,
    themeOverride: buildThemeOverride(cell),
  };
};
